import { ExcalidrawElement } from "./element/types";
import { newElementWith } from "./element/mutateElement";
import {BoundingBox, getCommonBoundingBox, getCommonBounds} from "./element/bounds";
import { getMaximumGroups } from "./groups";
import potpack from "potpack";
import { sortBy } from "lodash";
import { getBoundTextElement, getContainerElement } from "./element/textElement";
import { isTextElement } from "./element/typeChecks";
import Scene from "./scene/Scene";
import { ShapeCache } from "./scene/ShapeCache";
import Global from "../../../src/global";

export interface Alignment {
  position: "start" | "center" | "end" | "horizontal" | "vertical" | "grid";
  axis: "x" | "y" | "arrange";
}

export const alignElements = (
    disorderSelectedElements: ExcalidrawElement[],
  alignment: Alignment,
): ExcalidrawElement[] => {
  // Filter out bound text elements - they should move with their containers, not participate in layout
  const boundTextIds = new Set<string>();
  disorderSelectedElements.forEach(el => {
    if (isTextElement(el) && getContainerElement(el)) {
      boundTextIds.add(el.id);
    }
  });

  // Only layout non-bound-text elements
  let selectedElements = disorderSelectedElements.filter(el => !boundTextIds.has(el.id));

  // Helper function to update element and its bound text together
  const updateElementWithBoundText = (
      element: ExcalidrawElement,
      newX: number,
      newY: number,
  ): ExcalidrawElement[] => {
    const deltaX = newX - element.x;
    const deltaY = newY - element.y;

    // Clear all caches for the original element to force re-render
    ShapeCache.delete(element);
    delete Global.g_elementWithCanvasCache00000[element.id];

    const updatedElement = newElementWith(element, {
      x: newX,
      y: newY,
    });

    // Update bound text if exists
    const boundText = getBoundTextElement(element);
    if (boundText) {
      ShapeCache.delete(boundText);
      delete Global.g_elementWithCanvasCache00000[boundText.id];
      const updatedBoundText = newElementWith(boundText, {
        x: boundText.x + deltaX,
        y: boundText.y + deltaY,
      });
      return [updatedElement, updatedBoundText];
    }

    return [updatedElement];
  };

  if (alignment.axis === "arrange") {
    const gridAlign = alignment.position === "grid";
    const horizontalAlign = alignment.position === "horizontal";
    const verticalAlign = alignment.position === "vertical";
    if (horizontalAlign) {
      selectedElements = sortBy(selectedElements, ["x"]);
    } else if (verticalAlign) {
      selectedElements = sortBy(selectedElements, ["y"]);
    }

    const groups: ExcalidrawElement[][] = getMaximumGroups(selectedElements);
    const groupsBoundingBox = groups.map((group, index) => {
      const boundingBox = getCommonBoundingBox(group);
      return {
        ...boundingBox,
        width: boundingBox.width,
        height: boundingBox.height,
        x: boundingBox.minX,
        y: boundingBox.minY,
        group,
        index,
      };
    });

    const [x1, y1] = getCommonBounds(selectedElements);

    if (gridAlign) {
      const potpackBoxes = groupsBoundingBox.map((boundingBox) => {
        return {
          w: boundingBox.width + 20,
          h: boundingBox.height + 20,
          index: boundingBox.index,
        };
      });

      potpack(potpackBoxes);
      const firstPotpackGroup = groupsBoundingBox.find(
          (item) => item.index === potpackBoxes[0].index,
      )!;

      const delta = {
        x: firstPotpackGroup.x - x1,
        y: firstPotpackGroup.y - y1,
      };

      const a = potpackBoxes.flatMap((potpackBox) => {
        const box = groupsBoundingBox.find(
            (item) => item.index === potpackBox.index,
        )!;
        const { group } = box;
        return group.flatMap((element) => {
          const newX = firstPotpackGroup.x + potpackBox.x + element.x - box.x - delta.x;
          const newY = firstPotpackGroup.y + potpackBox.y + element.y - box.y - delta.y;
          return updateElementWithBoundText(element, newX, newY);
        });
      });

      return a;
    }
    const baseBox = groupsBoundingBox[0];
    let preBox = { width: 0, height: 0 };
    const delta = {
      x: baseBox.x - x1,
      y: baseBox.y - y1,
    };

    return groupsBoundingBox.flatMap((box, index) => {
      const { group } = box;
      if (!index) {
        return group.flatMap((element) => {
          const newX = element.x - delta.x;
          const newY = element.y - delta.y;
          return updateElementWithBoundText(element, newX, newY);
        });
      }
      preBox = {
        width: preBox.width + groupsBoundingBox[index - 1].width + 20,
        height: preBox.height + groupsBoundingBox[index - 1].height + 20,
      };

      return group.flatMap((element) => {
        const x = horizontalAlign
            ? baseBox.x + preBox.width + element.x - box.x
            : baseBox.x + element.x - box.x;
        const y = horizontalAlign
            ? baseBox.y + element.y - box.y
            : baseBox.y + preBox.height + element.y - box.y;
        const newX = x - delta.x;
        const newY = y - delta.y;
        return updateElementWithBoundText(element, newX, newY);
      });
    });
  }

  const groups: ExcalidrawElement[][] = getMaximumGroups(selectedElements);
  const selectionBoundingBox = getCommonBoundingBox(selectedElements);

  return groups.flatMap((group) => {
    const translation = calculateTranslation(
      group,
      selectionBoundingBox,
      alignment,
    );
    return group.flatMap((element) => {
      const newX = element.x + translation.x;
      const newY = element.y + translation.y;
      return updateElementWithBoundText(element, newX, newY);
    });
  });
};

const calculateTranslation = (
  group: ExcalidrawElement[],
  selectionBoundingBox: BoundingBox,
  { axis, position }: Alignment,
): { x: number; y: number } => {
  const groupBoundingBox = getCommonBoundingBox(group);

  const [min, max]: ["minX" | "minY", "maxX" | "maxY"] =
    axis === "x" ? ["minX", "maxX"] : ["minY", "maxY"];

  const noTranslation = { x: 0, y: 0 };
  if (position === "start") {
    return {
      ...noTranslation,
      [axis]: selectionBoundingBox[min] - groupBoundingBox[min],
    };
  } else if (position === "end") {
    return {
      ...noTranslation,
      [axis]: selectionBoundingBox[max] - groupBoundingBox[max],
    };
  } // else if (position === "center") {
  return {
    ...noTranslation,
    [axis]:
      (selectionBoundingBox[min] + selectionBoundingBox[max]) / 2 -
      (groupBoundingBox[min] + groupBoundingBox[max]) / 2,
  };
};
